import invariant from 'invariant'
import * as React from 'react'
import { useFormContext } from 'react-hook-form'
import { throttle } from 'throttle-debounce'

import { Override } from '~/utilities/types'

import { FieldElementWrapperProps } from '../field-wrapper/FieldWrapper'
import { InlineMessage } from '../inline-message/InlineMessage'
import { PasswordField } from '../password-field/PasswordField'
import { PasswordInput } from '../password-input/PasswordInput'

type ValidationResult = Record<string, boolean>

type CreatePasswordFieldProps = Override<
  React.ComponentProps<typeof PasswordInput> & FieldElementWrapperProps,
  {
    label?: string
    name?: string
    validate: (
      password: string
    ) => Promise<ValidationResult | undefined> | ValidationResult
    defaultValidation: ValidationResult
    messageDirection?: 'row' | 'column'
  }
>

export const CreatePasswordField = ({
  validate,
  defaultValidation,
  messageDirection = 'row',
  label = 'Create a password',
  name = 'password',
  className,
  validation,
  appearance,
  ...remainingProps
}: CreatePasswordFieldProps) => {
  const { formState, control } = useFormContext()
  const [isFocused, setIsFocused] = React.useState<boolean>(false)
  const [validationResult, setValidationResult] =
    React.useState<ValidationResult>(defaultValidation)

  const touched: boolean = formState.touched[name]
  const error =
    formState.errors[name]?.type === 'validate' ||
    Object.values(validationResult).every((isValid) => !isValid)

  const validatePassword = React.useCallback(
    async (password: string) => {
      const result = await validate(password)

      if (result) {
        invariant(
          typeof result === 'object',
          'The validate function must return an object'
        )

        setValidationResult(result)
        return Object.values(result).every((isValid) => isValid)
      }

      return false
    },
    [setValidationResult]
  )

  const handleChange = React.useCallback(throttle(500, validatePassword), [
    validatePassword
  ])

  const getMessageTheme = (result: boolean, isFocused: boolean) => {
    if (result) return 'success'

    return isFocused ? 'neutral' : 'error'
  }

  const showValidation: boolean =
    (control.mode.isOnBlur && (isFocused || touched)) ||
    (control.mode.isOnSubmit && (isFocused || (formState.isSubmitted && error)))

  return (
    <div className={className}>
      <PasswordField
        label={label}
        name={name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          handleChange(e.target.value)
        }
        onBlur={() => setIsFocused(false)}
        onFocus={() => setIsFocused(true)}
        validation={{ ...validation, validate: validatePassword }}
        appearance={appearance}
        {...remainingProps}
      />
      {showValidation && (
        <div
          style={{ '--direction': messageDirection }}
          className="direction-(--direction) mt-2 flex flex-wrap gap-2"
        >
          {Object.entries(validationResult).map(([message, result]) => (
            <InlineMessage
              key={message}
              theme={getMessageTheme(result, isFocused)}
            >
              {message}
            </InlineMessage>
          ))}
        </div>
      )}
    </div>
  )
}

CreatePasswordField.displayName = 'CreatePasswordField'
